"use client";

import { addToCartAction } from "@/actions/cart";
import type { ProductBrowseResult } from "@/dao/product";
import { useTranslations } from "@/i18n/client";
import { cn } from "@/lib/utils";
import { useCart } from "@/ui/hooks/use-cart";
import { Button } from "@/ui/shadcn/button";
import { useCartModal } from "@/zustand/storefront";
import { Loader2Icon } from "lucide-react";
import { useRouter } from "next/navigation";
import { type CSSProperties, useTransition } from "react";

export const AddToCartButton = ({
	variant,
	product,
	domain,
	disabled,
	outOfStock,
	className,
	style,
}: {
	variant?: ProductBrowseResult["data"][number]["variants"][number];
	product: ProductBrowseResult["data"][number];
	domain: string;
	outOfStock?: boolean;
	style: CSSProperties;
	disabled?: boolean;
	className?: string;
}) => {
	const t = useTranslations("Global.addToCart");
	const [pending, startTransition] = useTransition();
	const router = useRouter();
	const isDisabled = disabled || pending || !variant;
	const { openCartModal } = useCartModal();

	const { dispatch } = useCart();

	return (
		<div>
			<Button
				id="button-add-to-cart"
				size="lg"
				type="submit"
				className={cn("rounded-full w-full text-lg relative", className)}
				style={style}
				onClick={async (e) => {
					if (isDisabled) {
						e.preventDefault();
						return;
					}

					openCartModal();

					startTransition(async () => {
						dispatch({ type: "ADD_ITEM", product, variant });
						await addToCartAction({ variantId: variant.id, domain });
						router.refresh();
					});
				}}
				aria-disabled={isDisabled}
				aria-describedby="add-to-cart-msg"
				disabled={isDisabled}
			>
				<span className={cn("transition-opacity ease-in", pending ? "opacity-0" : "opacity-100")}>
					{outOfStock === true ? t("disabled") : t("actionButton")}
				</span>
				<span
					className={cn(
						"ease-out transition-opacity pointer-events-none absolute z-10",
						pending ? "opacity-100" : "opacity-0",
					)}
				>
					<Loader2Icon className="h-4 w-4 animate-spin" />
				</span>
			</Button>
			<p
				aria-hidden={disabled}
				className="mt-2 text-center text-muted-foreground text-xs"
				id="add-to-cart-msg"
			>
				{disabled ? "No variant selected" : " "}
			</p>
		</div>
	);
};
